import React, { createContext, useContext, useState, useEffect } from 'react';
import settingsService from '../services/settingsService';
import tenantService from '../services/tenantService';
import { getTenantSubdomain } from '../utils/tenantUtils';

const BrandingContext = createContext(null);

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    // Return defaults if context not available
    return {
      appName: 'Zerberos',
      tagline: 'Purple Team Operations Platform',
      logo: null,
      logoLight: null,
      primaryColor: '#9333ea', // Default purple
      secondaryColor: '#ef4444',
      footerText: '',
      loading: false
    };
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState({
    appName: 'Zerberos',
    tagline: 'Purple Team Operations Platform',
    logo: null,
    logoLight: null,
    primaryColor: '#9333ea',
    secondaryColor: '#ef4444',
    footerText: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      // Check if we're on a tenant subdomain
      const subdomain = getTenantSubdomain();

      if (subdomain) {
        // Resolve tenant branding (public endpoint, no auth needed)
        try {
          const tenant = await tenantService.resolveTenant(subdomain);
          if (tenant?.branding) {
            setBranding(prev => ({
              ...prev,
              appName: tenant.branding.appName || prev.appName,
              tagline: tenant.branding.tagline || prev.tagline,
              logo: tenant.branding.logo || null,
              logoLight: tenant.branding.logoLight || null,
              primaryColor: tenant.branding.primaryColor || prev.primaryColor,
              secondaryColor: tenant.branding.secondaryColor || prev.secondaryColor,
              footerText: tenant.branding.footerText || ''
            }));

            if (tenant.branding.primaryColor) {
              updateCSSVariables(tenant.branding.primaryColor, tenant.branding.secondaryColor);
            }
            return; // Don't fall through to global settings
          }
        } catch (tenantError) {
          console.warn('Could not resolve tenant branding, falling back to global:', tenantError.message);
        }
      }

      // Fall back to global settings (super admin portal or no tenant)
      const settings = await settingsService.getSettings();
      if (settings?.branding) {
        setBranding(prev => ({
          ...prev,
          appName: settings.branding.appName || prev.appName,
          tagline: settings.branding.tagline || prev.tagline,
          logo: settings.branding.logo || null,
          logoLight: settings.branding.logoLight || null,
          primaryColor: settings.branding.primaryColor || prev.primaryColor,
          secondaryColor: settings.branding.secondaryColor || prev.secondaryColor,
          footerText: ''
        }));

        // Update CSS custom properties for dynamic theming
        if (settings.branding.primaryColor) {
          updateCSSVariables(settings.branding.primaryColor, settings.branding.secondaryColor);
        }
      }
    } catch (error) {
      console.error('Error fetching branding:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateCSSVariables = (primary, secondary) => {
    const root = document.documentElement;

    // Convert hex to RGB for use in Tailwind's opacity utilities
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };

    const primaryRgb = hexToRgb(primary);
    const secondaryRgb = hexToRgb(secondary || '#ef4444');

    if (primaryRgb) {
      root.style.setProperty('--color-primary', primary);
      root.style.setProperty('--color-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
    }
    if (secondaryRgb) {
      root.style.setProperty('--color-secondary', secondary);
      root.style.setProperty('--color-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
    }
  };

  const refreshBranding = () => {
    fetchBranding();
  };

  // Helper to get inline style for primary color
  const getPrimaryStyle = (opacity = 1) => ({
    color: branding.primaryColor,
    opacity
  });

  const getPrimaryBgStyle = (opacity = 1) => ({
    backgroundColor: branding.primaryColor,
    opacity
  });

  return (
    <BrandingContext.Provider value={{
      ...branding,
      loading,
      refreshBranding,
      getPrimaryStyle,
      getPrimaryBgStyle
    }}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
